import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './RuleLibrary.css'

const RuleLibrary = () => {
  const { apiFetch } = useAuth()

  const [library, setLibrary]         = useState([])
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState(null)
  const [saving, setSaving]               = useState(false)
  const [deletingId, setDeletingId]       = useState(null)
  const [retranslatingId, setRetranslatingId] = useState(null)
  const [success, setSuccess]             = useState(null)

  // New rule form
  const [newText, setNewText]         = useState('')
  const [newImageUrl, setNewImageUrl] = useState(null)

  // Edit rule
  const [editingId, setEditingId]     = useState(null)
  const [editText, setEditText]       = useState('')
  const [editImageUrl, setEditImageUrl] = useState(null)
  const [editSaving, setEditSaving]   = useState(false)

  // Image library picker modal
  const [showImageModal, setShowImageModal]   = useState(false)
  const [imageModalTarget, setImageModalTarget] = useState('new') // 'new' | 'edit'
  const [imageLibrary, setImageLibrary]       = useState([])
  const [loadingImages, setLoadingImages]     = useState(false)
  const [uploadingLib, setUploadingLib]       = useState(false)
  const libFileInputRef                       = useRef(null)

  // ── Load rule library ─────────────────────────────────────────────────────
  const loadLibrary = () => {
    apiFetch('/api/admin/rule-library')
      .then(r => r.json())
      .then(data => setLibrary(Array.isArray(data) ? data : []))
      .catch(() => setError('Regelbibliothek konnte nicht geladen werden'))
      .finally(() => setLoading(false))
  }

  useEffect(loadLibrary, [])

  // ── Image library modal ───────────────────────────────────────────────────
  const openImageModal = async (target = 'new') => {
    setImageModalTarget(target)
    setShowImageModal(true)
    setLoadingImages(true)
    try {
      const res  = await apiFetch('/api/admin/image-library')
      const data = await res.json()
      setImageLibrary(Array.isArray(data) ? data : [])
    } catch { setImageLibrary([]) }
    finally  { setLoadingImages(false) }
  }

  const resizeImage = (file, maxW = 800, maxH = 600) =>
    new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(url)
        const scale = Math.min(1, maxW / img.width, maxH / img.height)
        const w = Math.round(img.width  * scale)
        const h = Math.round(img.height * scale)
        const canvas = document.createElement('canvas')
        canvas.width  = w
        canvas.height = h
        const isPng = file.type === 'image/png'
        canvas.getContext('2d').drawImage(img, 0, 0, w, h)
        const dataUrl = isPng ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.85)
        resolve({ base64: dataUrl.split(',')[1], contentType: isPng ? 'image/png' : 'image/jpeg' })
      }
      img.onerror = reject
      img.src = url
    })

  const handleLibraryUpload = async (file) => {
    if (!file) return
    setUploadingLib(true)
    try {
      const { base64, contentType } = await resizeImage(file)
      const res    = await apiFetch('/api/admin/upload-image', {
        method: 'POST',
        body: JSON.stringify({
          tenantSlug:  'shared',
          fileName:    file.name,
          fileData:    base64,
          contentType,
          addToShared: true,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      const libRes  = await apiFetch('/api/admin/image-library')
      const libData = await libRes.json()
      setImageLibrary(Array.isArray(libData) ? libData : [])
    } catch (err) {
      setError(`Upload fehlgeschlagen: ${err.message}`)
    } finally {
      setUploadingLib(false)
    }
  }

  // ── Save new library rule ─────────────────────────────────────────────────
  const handleSave = async () => {
    if (!newText.trim()) { setError('Bitte Text eingeben'); return }
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const res  = await apiFetch('/api/admin/rule-library', {
        method: 'POST',
        body: JSON.stringify({ text: newText.trim(), image_url: newImageUrl }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Fehler beim Speichern')
      setLibrary(prev => [data, ...prev])
      setNewText('')
      setNewImageUrl(null)
      setSuccess('Regel gespeichert')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  // ── Retranslate library rule ──────────────────────────────────────────────
  const handleRetranslate = async (id) => {
    setRetranslatingId(id)
    setError(null)
    setSuccess(null)
    try {
      const res  = await apiFetch(`/api/admin/rule-library/${id}/retranslate`, { method: 'POST' })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setLibrary(prev => prev.map(r => r.id === id ? data : r))
      setSuccess('Übersetzungen aktualisiert')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(`Übersetzung fehlgeschlagen: ${err.message}`)
    } finally {
      setRetranslatingId(null)
    }
  }

  // ── Delete library rule ───────────────────────────────────────────────────
  const handleDelete = async (id) => {
    setDeletingId(id)
    try {
      await apiFetch(`/api/admin/rule-library/${id}`, { method: 'DELETE' })
      setLibrary(prev => prev.filter(r => r.id !== id))
    } catch (err) {
      setError(`Löschen fehlgeschlagen: ${err.message}`)
    } finally {
      setDeletingId(null)
    }
  }

  // ── Edit existing rule ────────────────────────────────────────────────────
  const startEdit = (rule) => {
    setEditingId(rule.id)
    setEditText(rule.translations?.de || '')
    setEditImageUrl(rule.image_url || null)
    setError(null)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditText('')
    setEditImageUrl(null)
  }

  const handleEditSave = async (id) => {
    if (!editText.trim()) { setError('Bitte Text eingeben'); return }
    setEditSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const res  = await apiFetch(`/api/admin/rule-library/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ text: editText.trim(), image_url: editImageUrl }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Fehler beim Speichern')
      setLibrary(prev => prev.map(r => r.id === id ? data : r))
      setSuccess('Regel aktualisiert')
      setTimeout(() => setSuccess(null), 3000)
      cancelEdit()
    } catch (err) {
      setError(err.message)
    } finally {
      setEditSaving(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Regelbibliothek</h1>
          <p>Vorgefertigte Regeln, die automatisch in alle Sprachen übersetzt werden</p>
        </div>
      </div>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ {success}</div>}

      {/* ── Add new rule ───────────────────────────────────────────────── */}
      <div className="card rl-add-card">
        <div className="section-title">Neue Regel hinzufügen</div>
        <p className="rl-hint">
          Text auf Deutsch eingeben — wird automatisch in alle 26 Sprachen übersetzt.
        </p>

        <div className="form-group">
          <label>Regeltext (Deutsch)</label>
          <textarea
            className="form-control"
            value={newText}
            onChange={e => setNewText(e.target.value)}
            placeholder="z.B. Schutzhelm muss getragen werden"
            rows={2}
          />
        </div>

        {/* Image */}
        <div className="rl-image-picker">
          {newImageUrl ? (
            <div className="rl-image-preview">
              <img src={newImageUrl} alt="Vorschau" className="rl-preview-img" />
              <button className="btn btn-ghost rl-remove-btn" onClick={() => setNewImageUrl(null)}>
                ✕ Bild entfernen
              </button>
            </div>
          ) : (
            <button className="btn btn-ghost" onClick={() => openImageModal('new')}>
              🖼 Bild auswählen (optional)
            </button>
          )}
        </div>

        <div className="rl-save-row">
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !newText.trim()}>
            {saving
              ? <><span className="spinner spinner-sm" /> Übersetzen & Speichern...</>
              : '+ Regel speichern'}
          </button>
        </div>
      </div>

      {/* ── Library list ───────────────────────────────────────────────── */}
      <div className="card">
        <div className="section-title">Vorhandene Regeln ({library.length})</div>

        {loading ? (
          <div className="loading-state"><span className="spinner" /></div>
        ) : library.length === 0 ? (
          <p className="rl-empty">Noch keine Regeln in der Bibliothek.</p>
        ) : (
          <div className="rl-list">
            {library.map(rule => (
              <div key={rule.id} className="rl-rule-row">
                {editingId === rule.id ? (
                  /* ── Inline edit form ── */
                  <div>
                    <div className="form-group rl-edit-form-group">
                      <label className="rl-edit-label">Regeltext (Deutsch)</label>
                      <textarea
                        className="form-control rl-edit-textarea"
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        rows={2}
                      />
                    </div>
                    <div className="rl-edit-image-picker">
                      {editImageUrl ? (
                        <div className="rl-image-preview">
                          <img src={editImageUrl} alt="Vorschau" className="rl-preview-img" />
                          <button className="btn btn-ghost rl-remove-btn" onClick={() => setEditImageUrl(null)}>
                            ✕ Bild entfernen
                          </button>
                        </div>
                      ) : (
                        <button className="btn btn-ghost" onClick={() => openImageModal('edit')}>
                          Bild auswählen (optional)
                        </button>
                      )}
                    </div>
                    <div className="rl-edit-actions">
                      <button className="btn btn-ghost rl-edit-btn" onClick={cancelEdit} disabled={editSaving}>
                        Abbrechen
                      </button>
                      <button className="btn btn-primary rl-edit-btn" onClick={() => handleEditSave(rule.id)} disabled={editSaving || !editText.trim()}>
                        {editSaving
                          ? <><span className="spinner spinner-xs" /> Speichern...</>
                          : 'Speichern'}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Normal row ── */
                  <div className="rl-row-content">
                    {rule.image_url && (
                      <img src={rule.image_url} alt="" className="rl-row-img" />
                    )}
                    <div className="rl-row-text-wrap">
                      <div className="rl-row-text">{rule.translations?.de || '—'}</div>
                      <div className="rl-row-langs">{Object.keys(rule.translations || {}).length} Sprachen</div>
                    </div>
                    <button
                      className="rule-btn"
                      onClick={() => startEdit(rule)}
                      disabled={retranslatingId === rule.id || deletingId === rule.id}
                      title="Bearbeiten"
                    >
                      ✏
                    </button>
                    <button
                      className="rule-btn"
                      onClick={() => handleRetranslate(rule.id)}
                      disabled={retranslatingId === rule.id || deletingId === rule.id}
                      title="Übersetzungen neu generieren"
                    >
                      {retranslatingId === rule.id ? <span className="spinner spinner-xs" /> : '🔄'}
                    </button>
                    <button
                      className="rule-btn rule-btn-delete"
                      onClick={() => handleDelete(rule.id)}
                      disabled={deletingId === rule.id || retranslatingId === rule.id}
                      title="Löschen"
                    >
                      {deletingId === rule.id ? '...' : '✕'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Image library modal ─────────────────────────────────────────── */}
      {showImageModal && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="modal modal--wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bild auswählen</h2>
              <button className="modal-close" onClick={() => setShowImageModal(false)}>✕</button>
            </div>

            <div className="image-lib-upload">
              <input
                type="file" accept="image/*" className="input-hidden"
                ref={libFileInputRef}
                onChange={e => handleLibraryUpload(e.target.files[0])}
              />
              <button
                className="btn btn-ghost"
                onClick={() => libFileInputRef.current?.click()}
                disabled={uploadingLib}
              >
                {uploadingLib
                  ? <><span className="spinner spinner-xs" /> Wird hochgeladen...</>
                  : '⬆ Neues Bild hochladen'
                }
              </button>
            </div>

            {loadingImages ? (
              <div className="loading-state"><span className="spinner" /></div>
            ) : imageLibrary.length === 0 ? (
              <p className="rl-modal-empty">Noch keine Bilder in der Bibliothek.</p>
            ) : (
              <div className="image-lib-grid">
                {imageLibrary.map(img => (
                  <div
                    key={img.id}
                    className="image-lib-item"
                    onClick={() => {
                      if (imageModalTarget === 'edit') setEditImageUrl(img.url)
                      else setNewImageUrl(img.url)
                      setShowImageModal(false)
                    }}
                  >
                    <img src={img.url} alt={img.name} />
                    <div className="image-lib-item__name">{img.name}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowImageModal(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      <VersionHistoryPanel table="rule_library" global onRestored={loadLibrary} />
    </div>
  )
}

export default RuleLibrary
